import { useCallback, useMemo } from 'react';

import { useHistory, useLocation } from 'react-router';
import mapValues from 'lodash/mapValues';
import isEqual from 'lodash/isEqual';

import {
    parseQueryParams,
    QueryParamSpec,
    QueryParamsSpec,
    QueryParamValueType,
    updateQueryParams
} from '../utils/parseQueryParams';
import { useDeepMemo } from './useDeepMemo';

/**
 * Represents a set of query parameters as they are returned from {@link useQueryParams}.
 */
type UseQueryParams<S extends QueryParamsSpec> = {
    readonly [K in keyof S]: UseQueryParam<S[K]>;
};

/**
 * Represents a single query parameter.
 */
type UseQueryParam<P extends QueryParamSpec> = {
    /**
     * The current value of the query parameter.
     */
    readonly value: QueryParamValueType<P>;

    /**
     * Sets a new value for the query parameter.
     * Setting a new value causes a location change, if the new value is different from the current value.
     * The new value is deeply compared against the current value in order to avoid unnecessary location changes.
     * The new location is pushed onto the history stack, unless the `replace` parameter is set to true,
     * in which case the new location replaces the current location on the history stack.
     */
    readonly set: (newValue: QueryParamValueType<P>, replace?: boolean) => void;
};

/**
 * A custom hook which provides read and write access to a subset of the current query params,
 * which are specified by the given `spec`.
 *
 * This hook is mainly a wrapper for the {@link parseQueryParams} and {@link serializeQueryParams} functions.
 *
 * NOTE: In order to get proper type inference, the spec should be specified inline,
 * so the TypeScript compiler can infer the resulting query parameter types:
 *
 * Correct:
 * ```
 * const { foo, bar } = useQueryParams({ foo: 'integer', bar: 'boolean[]' });
 *
 * console.log(foo.value); // foo.value has a type of `number | undefined`
 * console.log(bar.value); // bar.value has a type of `ReadonlyArray<boolean>`
 * ```
 *
 * Wrong:
 * ```
 * const MY_PARAMS = { foo: 'integer', bar: 'boolean[]' };
 *
 * // ...
 *
 * const { foo, bar } = useQueryParams(MY_PARAMS);
 *
 * console.log(foo.value); // type of foo.value can not be inferred
 * console.log(bar.value); // type of bar.value can not be inferred
 * ```
 */
export function useQueryParams<S extends QueryParamsSpec>(specParam: S): UseQueryParams<S> {
    const spec = useDeepMemo(specParam);
    const { search } = useLocation();
    const { push, replace: replaceHistory } = useHistory();

    const parsedParams = useMemo(() => parseQueryParams(search, spec), [search, spec]);

    const setQueryParam = useCallback(
        <K extends keyof S>(name: K, newValue: QueryParamValueType<S[K]>, replace: boolean = false) => {
            const currentValue = parsedParams[name];

            if (isEqual(currentValue, newValue)) {
                return;
            }

            const newParams = {
                ...parsedParams,
                [name]: newValue
            };

            const navigate = replace ? replaceHistory : push;
            navigate({ search: updateQueryParams(search, newParams) });
        },
        [parsedParams, search, push, replaceHistory]
    );

    return useMemo(() => {
        return mapValues(parsedParams, <K extends keyof S>(value: QueryParamValueType<S[K]>, name: K) => ({
            value,
            set: (newValue, replace) => setQueryParam(name, newValue, replace)
        }));
    }, [parsedParams, setQueryParam]);
}
